// src/contexts/DarkModeContext.js
import React, {createContext, useState, useEffect, useContext} from "react";

export const DarkModeContext = createContext({
    isDarkMode: false,
    toggleDarkMode: () => {
    }
});

export const DarkModeProvider = ({children}) => {
    const [isDarkMode, setIsDarkMode] = useState(() => {
        const storedPreference = localStorage.getItem("darkMode");
        if (storedPreference !== null) {
            return storedPreference === "true";
        }
        return window.matchMedia("(prefers-color-scheme: dark)").matches;
    });

    // Apply dark mode effect
    useEffect(() => {
        if (isDarkMode) {
            document.documentElement.classList.add("dark");
        } else {
            document.documentElement.classList.remove("dark");
        }
        localStorage.setItem("darkMode", isDarkMode);
        document.body.dataset.theme = isDarkMode ? "dark" : "light";

        // Update meta theme-color for mobile browsers
        const metaThemeColor = document.querySelector("meta[name='theme-color']");
        if (metaThemeColor) {
            metaThemeColor.setAttribute(
                "content",
                isDarkMode ? "#0f172a" : "#ffffff"
            );
        }
    }, [isDarkMode]);

    // System preference listener
    useEffect(() => {
        const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
        const handleChange = (e) => {
            if (localStorage.getItem("darkMode") === null) {
                setIsDarkMode(e.matches);
            }
        };

        if (mediaQuery.addEventListener) {
            mediaQuery.addEventListener("change", handleChange);
            return () => mediaQuery.removeEventListener("change", handleChange);
        } else {
            mediaQuery.addListener(handleChange);
            return () => mediaQuery.removeListener(handleChange);
        }
    }, []);

    const toggleDarkMode = () => {
        setIsDarkMode(prev => !prev);
    };

    return (
        <DarkModeContext.Provider value={{isDarkMode, toggleDarkMode}}>
            {children}
        </DarkModeContext.Provider>
    );
};

export const useDarkMode = () => useContext(DarkModeContext);
